/*
 * レイアウト作成料 自動追加
 * 設定: テーマ設定「レイアウト作成料 自動追加」→ layout/theme.liquid が window.layoutFeeConfig を出力
 *
 * 1) カート同期（全ページ）
 *    - ページ表示時（カートに商品がある場合）と、Dawn の cartUpdate イベント（商品追加・数量変更・削除）の後に
 *      /cart.js を取得し、対象商品（商品タイプ＋タグ）の有無と作成料行を突合する。
 *    - 対象商品あり → 作成料行（自動追加分）が無ければ /cart/add.js で追加、数量が1以外なら /cart/change.js で1に戻す。
 *    - 対象商品なし → 自動追加分の作成料行を削除。
 *    - 自動追加分は line item property `_auto_layout_fee` で識別する。手動で単体追加した作成料行
 *      （プロパティなし）は同期対象外＝消さない・数量も触らない。
 * 2) 商品ページの簡易合計（<layout-fee-summary>）: 部数入力とバリエーション切替に連動して
 *    「単価 × 部数 + 作成料 = 合計」を更新する。
 */
(() => {
  const config = window.layoutFeeConfig;
  if (!config || !config.variantId) return;

  const AUTO_PROPERTY = '_auto_layout_fee';
  const CACHE_KEY = 'layoutFeeTargetCache';

  /* ---------- 通貨表示 ---------- */
  const formatMoney = (cents, format = config.moneyFormat || '¥{{amount_no_decimals}}') => {
    const value = Number(cents) || 0;
    const withComma = (n, decimals) =>
      n.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount':
          return withComma(value / 100, 2);
        case 'amount_no_decimals':
          return withComma(Math.round(value / 100), 0);
        case 'amount_with_comma_separator':
          return withComma(value / 100, 2).replace(/,/g, '.').replace(/\.(\d\d)$/, ',$1');
        case 'amount_no_decimals_with_comma_separator':
          return withComma(Math.round(value / 100), 0).replace(/,/g, '.');
        default:
          return withComma(value / 100, 2);
      }
    });
  };

  /* ---------- 対象商品の判定（product_id → true/false） ---------- */
  const known = new Map();
  (config.targetIds || []).forEach((id) => id && known.set(String(id), true));
  (config.nonTargetIds || []).forEach((id) => id && known.set(String(id), false));
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
    Object.keys(cached).forEach((id) => {
      if (!known.has(id)) known.set(id, !!cached[id]);
    });
  } catch (e) {
    /* sessionStorage が使えない環境では都度判定する */
  }
  const persistKnown = () => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(known)));
    } catch (e) {
      /* noop */
    }
  };

  const isTargetItem = async (item) => {
    if (item.product_type !== config.targetType) return false;
    const id = String(item.product_id);
    if (known.has(id)) return known.get(id);
    // cart.js の行データにはタグが含まれないため、商品JSONを取得してタグを確認する（セッション内でキャッシュ）
    let result = false;
    try {
      const url = `${(item.url || '').split('?')[0]}.js`;
      const response = await fetch(url);
      const product = await response.json();
      result = Array.isArray(product.tags) && product.tags.includes(config.targetTag);
    } catch (e) {
      console.warn('[layout-fee] product fetch failed', item.handle, e);
      return false;
    }
    known.set(id, result);
    persistKnown();
    return result;
  };

  /* ---------- Cart API ---------- */
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const fetchCart = () => fetch(`${window.routes.cart_url}.js`, { headers: jsonHeaders }).then((r) => r.json());
  const addFee = () =>
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        items: [{ id: config.variantId, quantity: 1, properties: { [AUTO_PROPERTY]: '1' } }],
      }),
    }).then((r) => r.json());
  const changeLine = (key, quantity) =>
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());

  /* ---------- 画面の再描画（変更があったときだけ） ---------- */
  const getSectionInnerHTML = (html, selector) =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;

  const refreshUI = async (cart) => {
    const isEmpty = cart.item_count === 0;
    const cartItems = document.querySelector('cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    const cartDrawer = document.querySelector('cart-drawer');

    const sections = [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
    if (cartFooter?.dataset.id) sections.push({ id: 'main-cart-footer', section: cartFooter.dataset.id, selector: '.js-contents' });

    const tasks = [];
    if (cartItems && typeof cartItems.onCartUpdate === 'function') tasks.push(cartItems.onCartUpdate());
    tasks.push(
      fetch(`${window.routes.cart_url}?sections=${sections.map((s) => s.section).join(',')}`)
        .then((r) => r.json())
        .then((rendered) => {
          sections.forEach((section) => {
            const container = document.getElementById(section.id);
            const html = rendered[section.section];
            if (!container || !html) return;
            const target = container.querySelector(section.selector) || container;
            const inner = getSectionInnerHTML(html, section.selector);
            if (inner !== undefined) target.innerHTML = inner;
          });
        })
        .catch((e) => console.warn('[layout-fee] section render failed', e))
    );
    await Promise.all(tasks);

    if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
    if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
  };

  /* ---------- 同期本体 ---------- */
  let syncing = false;
  let rerunRequested = false;

  const sync = async () => {
    if (syncing) {
      rerunRequested = true;
      return;
    }
    syncing = true;
    try {
      const cart = await fetchCart();
      let targetCount = 0;
      for (const item of cart.items) {
        if (await isTargetItem(item)) targetCount += item.quantity;
      }
      const feeLines = cart.items.filter((item) => item.variant_id === config.variantId);
      const autoLines = feeLines.filter((item) => item.properties && item.properties[AUTO_PROPERTY]);
      const manualLines = feeLines.filter((item) => !(item.properties && item.properties[AUTO_PROPERTY]));

      let latestCart = null;
      if (targetCount > 0) {
        if (autoLines.length === 0 && manualLines.length === 0) {
          // 対象商品あり・作成料なし → 自動追加（数量1）
          await addFee();
          latestCart = await fetchCart();
        } else {
          // 自動追加分は常に1行・数量1。重複行があれば2行目以降は削除
          for (let i = 0; i < autoLines.length; i += 1) {
            const wanted = i === 0 ? 1 : 0;
            if (autoLines[i].quantity !== wanted) latestCart = await changeLine(autoLines[i].key, wanted);
          }
        }
      } else {
        // 対象商品なし → 自動追加分を削除（手動追加分は残す）
        for (const line of autoLines) latestCart = await changeLine(line.key, 0);
      }

      if (latestCart) await refreshUI(latestCart);
    } catch (e) {
      console.warn('[layout-fee] sync failed', e);
    } finally {
      syncing = false;
      if (rerunRequested) {
        rerunRequested = false;
        sync();
      }
    }
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'layout-fee') return;
      sync();
    });
  }
  if (config.cartItemCount > 0) sync();

  /* ---------- 商品ページの簡易合計 ---------- */
  if (!customElements.get('layout-fee-summary')) {
    customElements.define(
      'layout-fee-summary',
      class LayoutFeeSummary extends HTMLElement {
        connectedCallback() {
          known.set(String(this.dataset.layoutFeeTargetProduct), true);
          this.unitPrice = Number(this.dataset.unitPrice) || 0;
          this.feePrice = Number(this.dataset.feePrice) || config.price || 0;
          this.quantityInput = document.getElementById(`Quantity-${this.dataset.section}`);
          this.onQuantityChange = () => this.render();
          if (this.quantityInput) {
            this.quantityInput.addEventListener('change', this.onQuantityChange);
            this.quantityInput.addEventListener('input', this.onQuantityChange);
          }
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data.sectionId !== this.dataset.section) return;
              const variant = event.data.variant;
              if (!variant) return;
              this.unitPrice = Number(variant.price) || 0;
              this.render();
            });
          }
          this.render();
        }

        disconnectedCallback() {
          if (this.quantityInput) {
            this.quantityInput.removeEventListener('change', this.onQuantityChange);
            this.quantityInput.removeEventListener('input', this.onQuantityChange);
          }
          if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        }

        get quantity() {
          const value = parseInt(this.quantityInput?.value, 10);
          return Number.isFinite(value) && value > 0 ? value : 1;
        }

        render() {
          const quantity = this.quantity;
          const subtotal = this.unitPrice * quantity;
          const set = (name, text) => {
            const el = this.querySelector(`[data-layout-fee-${name}]`);
            if (el) el.textContent = text;
          };
          set('unit', formatMoney(this.unitPrice));
          set('qty', String(quantity));
          set('subtotal', formatMoney(subtotal));
          set('fee', formatMoney(this.feePrice));
          set('total', formatMoney(subtotal + this.feePrice));
        }
      }
    );
  }
})();
